/**
 * Deck materials — the VIEW model the source pane enumerates.
 *
 * A "material" is anything the pane can show as an item: a source from
 * `SyncDeckMeta.sources` (document, photo scan, audio or YouTube
 * transcription, pasted text), a note, or a mind map. This module is pure
 * and metadata-only: an item carries an identity, a display kind and a label
 * — never content, never previews. Both platforms enumerate the pane from it,
 * which is what keeps their segments, tiles and add menu in step.
 *
 * Persistence stays where it is (`sources` on the deck meta, notes in the
 * deck Y.Doc, the one `deckMindmaps` record). Nothing here is synced.
 */

/** Display kind — drives the icon on tiles, segments and the add menu. */
export type DeckMaterialKind =
  | 'document'
  | 'photos'
  | 'audio'
  | 'youtube'
  | 'text'
  | 'note'
  | 'mindmap';

/** Stable identity of one item. */
export type DeckMaterialRef =
  | { type: 'source'; sourceId: string }
  | { type: 'note'; noteId: string }
  | { type: 'mindmap'; mindmapId: string };

export interface DeckMaterialItem {
  ref: DeckMaterialRef;
  /** `encodeMaterialRef(ref)` — stable string key for lists and URLs. */
  key: string;
  kind: DeckMaterialKind;
  /** Display name, already resolved (filename, note/map title, or a
   *  pre-translated numbered fallback). */
  label: string;
  addedAtISO?: string;
  /** The entry's materials-order key, when it has one — what `listDeckMaterials`
   *  sorted by, and what a reorder plans its neighbours from. */
  sortKey?: string;
  /**
   * Still being uploaded and read — the deck does not hold it yet. Its tab
   * and tile exist so the user lands on it straight from page selection;
   * the pane shows a skeleton where the document will be. Cleared the
   * moment the source lands under the same id.
   */
  pending?: boolean;
  /**
   * The upload behind a pending item failed. The item stays listed — its
   * tile says so and offers Retry — rather than vanishing with a toast.
   */
  uploadFailed?: boolean;
}

/**
 * Entries of the "+" menu, in the design's order. `notes` and `mindmap` open
 * the deck's existing note / map (one of each today); the rest route into
 * the create-deck pipeline with the deck as the destination.
 */
export type AddMaterialType =
  | 'document'
  | 'photos'
  | 'mindmap'
  | 'audio'
  | 'record'
  | 'notes'
  | 'youtube';

/**
 * The id of a deck's mind map in the materials list. A deck has exactly one
 * synced mind map today (an LWW record keyed by deck path, no id of its own),
 * so the list addresses it by this constant until maps get real ids.
 */
export const DEFAULT_MINDMAP_ID = 'default';

/** Pre-translated fallback names — the DS and this module carry no i18n. */
export interface DeckMaterialLabels {
  /** Numbered fallback for a source with no usable name ("Source"). */
  source: string;
  /** Numbered fallback for a pasted-text source ("Text"). */
  text: string;
  /** Numbered fallback for a note ("Note"). */
  note: string;
  /** Numbered fallback for a mind map ("Mind map"). */
  mindmap: string;
}
